import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import settingsService from '../services/settingsService';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
    Cloud,
    LayoutDashboard,
    User,
    Settings,
    LogOut,
    ChevronLeft,
    Menu,
    HardDrive,
    ShieldCheck,
    BarChart2,
    Building2,
} from 'lucide-react';

const DashboardSidebar = ({ storageData, collapsed, onToggle }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const [appSettings, setAppSettings] = useState(null);

    useEffect(() => {
        settingsService.getSettings().then(setAppSettings).catch(() => { });
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const navItems = [
        { icon: BarChart2, label: 'Dashboard', path: '/stats' },
        { icon: LayoutDashboard, label: 'My Drive', path: '/dashboard' },
        { icon: Building2, label: 'Departemen', path: '/department' },
        { icon: User, label: 'Profil Saya', path: '/profile' },
        { icon: Settings, label: 'Pengaturan', path: '/settings' },
    ];

    const storage = storageData?.storage || storageData;
    const usedGB = storage ? (storage.used / (1024 ** 3)).toFixed(2) : '0';
    const totalGB = storage ? (storage.limit / (1024 ** 3)).toFixed(2) : '0';
    const pct = storage?.usagePercentage || 0;
    const barColor = pct > 85 ? '#ef4444' : pct > 60 ? '#f59e0b' : '#10b981';

    return (
        <>
            {/* Sidebar */}
            <aside
                style={{
                    width: collapsed ? '72px' : '260px',
                    height: '100vh',
                    position: 'sticky',
                    top: 0,
                    background: 'linear-gradient(180deg, #0f172a 0%, #1e3a5f 60%, #0f2744 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden',
                    flexShrink: 0,
                    boxShadow: '4px 0 24px rgba(15,23,42,0.18)',
                    zIndex: 30,
                }}
            >
                {/* Toggle Button */}
                <button
                    onClick={onToggle}
                    style={{
                        position: 'absolute',
                        top: '18px',
                        right: collapsed ? '50%' : '12px',
                        transform: collapsed ? 'translateX(50%)' : 'none',
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: '8px',
                        color: '#94a3b8',
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.25s ease',
                        zIndex: 10,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.16)'; e.currentTarget.style.color = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#94a3b8'; }}
                    title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    {collapsed ? <Menu size={16} /> : <ChevronLeft size={16} />}
                </button>

                {/* Logo Section */}
                <div style={{
                    padding: collapsed ? '18px 0' : '18px 20px',
                    paddingRight: collapsed ? '0' : '52px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    minHeight: '68px',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    borderBottom: '1px solid rgba(255,255,255,0.07)',
                }}>
                    {!collapsed && (
                        <img
                            src={appSettings?.logo_url || '/logo192.png'}
                            alt="Logo"
                            style={{ height: '36px', width: 'auto', objectFit: 'contain', flexShrink: 0 }}
                        />
                    )}
                    {!collapsed && (
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: '14px', lineHeight: 1.3, wordBreak: 'break-word' }}>
                                {appSettings?.app_name || 'Data Storage'}
                            </div>
                            <div style={{ color: '#38bdf8', fontWeight: 600, fontSize: '10px', letterSpacing: '0.08em', textTransform: 'uppercase', lineHeight: 1.4, marginTop: '2px', wordBreak: 'break-word' }}>
                                {appSettings?.app_subtitle || 'PDTI-UMI'}
                            </div>
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <nav style={{ padding: collapsed ? '16px 8px' : '16px 12px', flex: 1 }}>
                    {!collapsed && (
                        <div style={{ color: '#475569', fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px', paddingLeft: '8px' }}>
                            Navigasi
                        </div>
                    )}

                    {navItems.map(({ icon: Icon, label, path }) => {
                        const active = location.pathname === path;
                        return (
                            <button
                                key={path}
                                onClick={() => navigate(path)}
                                title={collapsed ? label : ''}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: collapsed ? '10px 0' : '10px 12px',
                                    justifyContent: collapsed ? 'center' : 'flex-start',
                                    borderRadius: '10px',
                                    border: 'none',
                                    marginBottom: '4px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    background: active
                                        ? 'linear-gradient(135deg, rgba(56,189,248,0.2), rgba(99,102,241,0.15))'
                                        : 'transparent',
                                    color: active ? '#38bdf8' : '#cbd5e1', // Changed from #94a3b8 to #cbd5e1
                                    fontWeight: active ? 600 : 500, // Slightly bolder for inactive too
                                    fontSize: '14px',
                                    outline: 'none',
                                    boxShadow: active ? 'inset 0 0 0 1px rgba(56,189,248,0.25)' : 'none',
                                    backdropFilter: active ? 'blur(4px)' : 'none',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                }}
                                onMouseEnter={e => {
                                    if (!active) {
                                        e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                                        e.currentTarget.style.color = '#f1f5f9'; // Brighter on hover
                                    }
                                }}
                                onMouseLeave={e => {
                                    if (!active) {
                                        e.currentTarget.style.background = 'transparent';
                                        e.currentTarget.style.color = '#cbd5e1'; // Back to new brighter color
                                    }
                                }}
                            >
                                <Icon size={18} style={{ flexShrink: 0 }} />
                                {!collapsed && <span>{label}</span>}
                            </button>
                        );
                    })}

                    {/* Admin link */}
                    {user?.role === 'admin' && (
                        <button
                            onClick={() => navigate('/admin')}
                            title={collapsed ? 'Admin Panel' : ''}
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: collapsed ? '10px 0' : '10px 12px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                borderRadius: '10px',
                                border: 'none',
                                marginBottom: '4px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                background: location.pathname === '/admin'
                                    ? 'linear-gradient(135deg, rgba(239,68,68,0.2), rgba(220,38,38,0.1))'
                                    : 'transparent',
                                color: location.pathname === '/admin' ? '#f87171' : '#fca5a5', // Changed from #f87171aa to #fca5a5
                                fontWeight: 500,
                                fontSize: '14px',
                                outline: 'none',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#fecaca'; }} // Lighter on hover
                            onMouseLeave={e => {
                                e.currentTarget.style.background = location.pathname === '/admin' ? 'linear-gradient(135deg, rgba(239,68,68,0.2), rgba(220,38,38,0.1))' : 'transparent';
                                e.currentTarget.style.color = location.pathname === '/admin' ? '#f87171' : '#fca5a5'; // Back to new color
                            }}
                        >
                            <ShieldCheck size={18} style={{ flexShrink: 0 }} />
                            {!collapsed && <span>Admin Panel</span>}
                        </button>
                    )}
                </nav>

                {/* Storage Mini */}
                {!collapsed && storage && (
                    <div style={{
                        padding: '12px 16px',
                        margin: '0 12px 12px',
                        borderRadius: '12px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.08)',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                            <div style={{
                                width: '28px', height: '28px', borderRadius: '8px',
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <HardDrive size={14} color="#fff" />
                            </div>
                            <div>
                                <div style={{ color: '#e2e8f0', fontSize: '12px', fontWeight: 600 }}>Penyimpanan</div>
                                <div style={{ color: '#64748b', fontSize: '10px' }}>{usedGB} / {totalGB} GB</div>
                            </div>
                            <div style={{ marginLeft: 'auto', fontSize: '12px', fontWeight: 700, color: barColor }}>
                                {pct.toFixed(0)}%
                            </div>
                        </div>
                        <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '999px', height: '5px', overflow: 'hidden' }}>
                            <div style={{
                                height: '100%',
                                width: `${Math.min(pct, 100)}%`,
                                background: `linear-gradient(90deg, ${barColor}, ${barColor}bb)`,
                                borderRadius: '999px',
                                transition: 'width 0.6s ease',
                            }} />
                        </div>
                    </div>
                )}

                {/* Storage icon only when collapsed */}
                {collapsed && storage && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                        <div title={`${pct.toFixed(0)}% terpakai`} style={{
                            width: '36px', height: '36px', borderRadius: '10px',
                            background: 'rgba(255,255,255,0.06)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: barColor,
                        }}>
                            <HardDrive size={16} />
                        </div>
                    </div>
                )}

                {/* Divider */}
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', margin: '0 16px' }} />

                {/* User Section */}
                <div style={{
                    padding: collapsed ? '12px 8px' : '14px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                }}>
                    <Avatar style={{ width: '34px', height: '34px', flexShrink: 0, cursor: 'pointer', border: '2px solid rgba(56,189,248,0.3)' }}>
                        <AvatarFallback style={{ background: 'linear-gradient(135deg, #38bdf8, #818cf8)', color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                            {user?.username?.charAt(0).toUpperCase() || 'U'}
                        </AvatarFallback>
                    </Avatar>
                    {!collapsed && (
                        <>
                            <div style={{ overflow: 'hidden', flex: 1 }}>
                                <div style={{ color: '#f1f5f9', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {user?.username || 'User'}
                                </div>
                                <div style={{ color: '#475569', fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {user?.email || ''}
                                </div>
                            </div>
                            <button
                                onClick={handleLogout}
                                title="Logout"
                                style={{
                                    background: 'rgba(239,68,68,0.1)',
                                    border: '1px solid rgba(239,68,68,0.2)',
                                    borderRadius: '8px',
                                    padding: '6px',
                                    cursor: 'pointer',
                                    color: '#f87171',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease',
                                    flexShrink: 0,
                                }}
                                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.2)'; e.currentTarget.style.color = '#fca5a5'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; e.currentTarget.style.color = '#f87171'; }}
                            >
                                <LogOut size={15} />
                            </button>
                        </>
                    )}
                </div>
            </aside>
        </>
    );
};

export default DashboardSidebar;
